import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import LinkedInService from './system/LinkedInService';
import './styles/LinkedInCallback.css';
import { useLanguage } from '../contexts/LanguageContext';

const LinkedInCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [status, setStatus] = useState(t('callback', 'processing'));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processAuth = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        
        // Hvis der ikke er nogen parametre, kan det være fordi denne komponent blev indlæst
        // direkte uden at være et resultat af OAuth flowet
        if (!code && !error) {
          // Tjek om der er en besked i location state (f.eks. når omdirigeret fra EmployeeRoute)
          const message = location.state?.message;
          if (message) {
            setError(message);
            setLoading(false);
            return;
          }
          
          // Ellers omdirigér til LinkedIn-indstillinger
          setTimeout(() => {
            navigate('/linkedin-settings');
          }, 2000);
          return;
        }

        // Tjek om der er fejl fra LinkedIn OAuth
        if (error) {
          throw new Error(`LinkedIn OAuth fejl: ${error} - ${errorDescription || 'Ingen fejlbeskrivelse'}`);
        }

        // Tjek om vi har modtaget en auth code
        if (!code) {
          throw new Error('Ingen autorisationskode modtaget fra LinkedIn');
        }

        // Tjek om brugeren er logget ind
        if (!auth.currentUser) {
          setStatus(t('callback', 'verifying'));
          // Vent op til 5 sekunder på at auth.currentUser bliver tilgængelig
          for (let i = 0; i < 10; i++) {
            await new Promise(resolve => setTimeout(resolve, 500));
            if (auth.currentUser) break;
          }
          
          if (!auth.currentUser) {
            throw new Error('Du skal være logget ind for at forbinde din LinkedIn-konto');
          }
        }

        const userId = auth.currentUser.uid;
        
        // Hent access token fra LinkedIn
        setStatus(t('callback', 'connecting'));
        try {
          const tokenData = await LinkedInService.getAccessToken(code);
          
          // Formater token data med korrekt tidsstempel
          const formattedTokenData = {
            accessToken: tokenData.accessToken,
            expiresIn: tokenData.expiresIn,
            updatedAt: new Date().toISOString()
          };

          // Kun tilføj refreshToken hvis det findes
          if (tokenData.refreshToken) {
            formattedTokenData.refreshToken = tokenData.refreshToken;
          }
          
          // Hent brugerens LinkedIn profil
          setStatus(t('callback', 'gettingProfile'));
          const profileData = await LinkedInService.getUserProfile(tokenData.accessToken);
          
          // Gem token og profildata i Firestore
          setStatus(t('callback', 'saving'));
          const success = await LinkedInService.saveLinkedInToken(userId, formattedTokenData, profileData);
          
          if (success) {
            setStatus(t('callback', 'connected'));
            // Fjern timeout og gå direkte til destination
            window.location.href = '/linkedin-settings';
          } else {
            throw new Error('Kunne ikke gemme LinkedIn-token');
          }
        } catch (tokenError) {
          // Specificer fejlmeddelelser baseret på fejltypen
          if (tokenError.message && tokenError.message.includes('Failed to fetch')) {
            throw new Error('Kunne ikke oprette forbindelse til LinkedIn API. Dette kan skyldes en netværksfejl eller CORS-begrænsninger. Prøv at rydde dine browsercookies eller brug en anden browser.');
          } else if (tokenError.message && tokenError.message.includes('CORS')) {
            throw new Error('CORS-fejl ved forbindelse til LinkedIn. Prøv at tilgå app.nuvik.io direkte i en ny fane, og forbind derefter LinkedIn.');
          } else {
            throw tokenError;
          }
        }
      } catch (err) {
        console.error('Fejl under LinkedIn-callback:', err);
        setError(err.message || 'Der opstod en fejl ved tilknytning af LinkedIn-konto');
      } finally {
        setLoading(false);
      }
    };

    processAuth();
  }, [location, navigate, t]);

  return (
    <div className="linkedin-callback-container">
      <div className="linkedin-callback-card">
        <div className="linkedin-header">
          <div className="linkedin-logo"></div>
        <h2>LinkedIn Integration</h2>
        </div>
        
        {loading ? (
          <div className="linkedin-loading">
            <div className="linkedin-spinner"></div>
            <h3>{status}</h3>
            <div className="linkedin-steps">
              <div className={`linkedin-step ${status === t('callback', 'processing') ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-text">{t('callback', 'loadingSteps.processing')}</div>
              </div>
              <div className={`linkedin-step ${status === t('callback', 'connecting') ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-text">{t('callback', 'loadingSteps.connecting')}</div>
              </div>
              <div className={`linkedin-step ${status === t('callback', 'gettingProfile') ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-text">{t('callback', 'loadingSteps.profile')}</div>
              </div>
              <div className={`linkedin-step ${status === t('callback', 'saving') ? 'active' : ''}`}>
                <div className="step-number">4</div>
                <div className="step-text">{t('callback', 'loadingSteps.finishing')}</div>
              </div>
            </div>
            <p className="linkedin-info-message">
              {t('callback', 'waitMessage')}
            </p>
          </div>
        ) : error ? (
          <div className="linkedin-error">
            <div className="linkedin-error-icon"></div>
            <h3>{t('callback', 'error.title')}</h3>
            <p>{error}</p>
            <div className="linkedin-error-help">
              <p>{t('callback', 'error.solutions.title')}</p>
              <ul>
                <li>{t('callback', 'error.solutions.clearCookies')}</li>
                <li>{t('callback', 'error.solutions.tryBrowser')}</li>
                <li>{t('callback', 'error.solutions.checkCookies')}</li>
                <li>{t('callback', 'error.solutions.useHttps')}</li>
              </ul>
            </div>
            <button onClick={() => navigate('/linkedin-settings')} className="linkedin-button">
              {t('callback', 'error.backButton')}
            </button>
          </div>
        ) : (
          <div className="linkedin-success">
            <div className="linkedin-success-icon"></div>
            <h3>{t('callback', 'success.title')}</h3>
            <p>{t('callback', 'success.message')}</p>
            <p>{t('callback', 'success.redirect')}</p>
            <div className="linkedin-progress-bar">
              <div className="linkedin-progress-fill"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedInCallback; 